/**
 * Imports
 *
 * We use vuex for global state management.
 * We use vuelidate for the form validation.
 *
 * @see https://vuex.vuejs.org/
 * @see https://vuelidate.netlify.com/#getting-started
 */
import Vue from 'vue';
import Vuex from 'vuex';
import Vuelidate from 'vuelidate';
import { has } from 'lodash';
import SignupForm from './components/form/SignupForm.vue';

Vue.use(Vuex);
Vue.use(Vuelidate);

/**
 * Signup
 *
 * @author PH
 */
export default class Signup {
  constructor(data) {
    this.data = data;
    this.storage = null;
    this.application = null;

    this._initVuex(data);
    this._initVue(data);
  }

  _initVue(data) {
    if (this.storage) {
      const store = this.storage;

      this.application = new Vue({
        el: '#signup-application',
        store,
        components: { SignupForm },
        render: h => h(SignupForm)
      });
    }
  }

  _initVuex(data) {
    this.storage = new Vuex.Store({
      state: {
        sections: data.sections
      },
      getters: {
        sections: state => {
          return state.sections;
        },

        getSection: state => id => {
          return state.sections.find(section => section.id === id);
        },

        getSectionIndex: state => id => {
          return state.sections.findIndex(section => section.id === id);
        },

        /**
         * Get: Section Data
         *
         * Function returns the specific section data. It
         * search section in the whole array and returns
         * the correct data.
         *
         * @author PH
         * @return mixed;
         */
        getSectionData: state => (id, key) => {
          const sectionIndex = this.storage.getters.getSectionIndex(id);
          if (-1 !== sectionIndex) {
            if (state.sections[sectionIndex].hasOwnProperty(key)) {
              return state.sections[sectionIndex][key];
            }
          }
        },

        /**
         * Get: Section Settings
         *
         * @author PH
         * @return {mixed}
         */
        getSectionSettings: state => (section, setting) => {
          return this.storage.getters.getSectionData(section, 'settings')[setting];
        },

        /**
         * Get: Field Value
         *
         * Function search the correct field in {section} based on
         * field {id} na returns the value.
         *
         * @author PH
         * @return mixed
         */
        getFieldValue: state => (id, section) => {
          if (id && section) {
            const location = this.storage.getters.getFieldLocation(id, section);
            if (location) {
              return state.sections[location.section].fields[location.field].value;
            }
          }
        },

        /**
         * Get: Field Data
         *
         * Function search the correct field  data in {section}
         * based on field {id}, {key} and returns the value.
         *
         * @author PH
         * @return mixed
         */
        getFieldData: state => (id, key, section) => {
          if (id && key && section) {
            const location = this.storage.getters.getFieldLocation(id, section);
            if (location) {
              if (state.sections[location.section].fields[location.field].hasOwnProperty(key)) {
                return state.sections[location.section].fields[location.field][key];
              }
            }
          }
        },

        /**
         * Get: Field Location
         *
         * Function returns the object with field location
         * in the sections array. It search based on section
         * and field ID.
         *
         * @author PH
         * @return object|nul;
         */
        getFieldLocation: state => (id, section) => {
          if (section && id) {
            const sectionIndex = this.storage.getters.getSectionIndex(section);
            if (sectionIndex !== -1) {
              const fieldIndex = state.sections[sectionIndex].fields.findIndex(field => field.id === id);
              if (fieldIndex !== -1) {
                return {
                  section: sectionIndex,
                  field: fieldIndex
                };
              }
            }
          }

          return false;
        },

        getFieldsBy: state => (propName, propValue, section) => {
          if (section && propName && propValue) {
            const sectionIndex = this.storage.getters.getSectionIndex(section);
            if (sectionIndex !== -1) {
              return this.storage.getters.sections[sectionIndex].fields.filter(field => field[propName] === propValue);
            }
          }

          return [];
        },

        getConditionalChecker: state  => ({field, condition, value}) => {
          if (! field || ! condition || ! value) {
            return undefined;
          }

          const location = this.storage.getters.getFieldLocation(
            field,
            field?.split("[")[0]
          );
          const fieldData = this.storage.getters.sections[location.section].fields[location.field];

          if (location && fieldData) {
            switch (condition.trim()) {
              case '===':
                return fieldData.value == value;
              case '!==':
                return fieldData.value != value;
              case 'in':
                return value.split(',').includes(fieldData.value);
              case 'not_in':
                return ! value.split(',').includes(fieldData.value);

            }
          }

          return undefined;
        }
      },
      mutations: {
        /**
         * Update: Input
         *
         * Function updates input in global state. It analyzes the
         * field location in sections data and updates the property
         * if exists in the object.
         *
         * @param state Object State Object
         * @param data  Object Object with field ID, section ID and the values object
         * @author PH
         * @return void
         */
        updateInput(state, data) {
          const location = this.getters.getFieldLocation(data.id, data.section);
          if (location && data.values) {
            Object.keys(data.values).forEach(key => {
              if (state.sections[location.section].fields[location.field].hasOwnProperty(key)) {
                state.sections[location.section].fields[location.field][key] = data.values[key];
              }
            });
          }
        },

        /**
         * Update: Section
         *
         * Function updates section in global state. It analyzes the
         * section location in global data and updates the property
         * if exists in the object.
         *
         * @param state Object State Object
         * @param data  Object Object with section ID and the values object
         * @author PH
         * @return void
         */
        updateSection(state, data) {
          if (data.section) {
            const sectionIndex = this.getters.getSectionIndex(data.section);
            if (-1 !== sectionIndex) {
              if (data.values) {
                Object.keys(data.values).forEach(key => {
                  if (state.sections[sectionIndex].hasOwnProperty(key)) {
                    state.sections[sectionIndex][key] = data.values[key];
                  }
                });
              }
            }
          }
        },

        /**
         * Update: Section Fields
         *
         * Function is used for updating fields in specific section. We can use that
         * for adding the new  fields or removing based on group ID.
         *
         * @author PH
         * @return void
         */
        updateSectionFields(state, data) {
          if (data.section) {
            const sectionIndex = this.getters.getSectionIndex(data.section);
            if (-1 !== sectionIndex) {
              if (data.fields) {
                // Add fields.
                data.fields.forEach(field => {
                  state.sections[sectionIndex]['fields'].push(field);
                });
              }

              if (data.remove && data.group) {
                // Remove fields.
                state.sections[sectionIndex].fields = state.sections[sectionIndex].fields.filter(field => {
                  return field.group !== data.group;
                });
              }
            }
          }
        },

        /**
         * Update: Section Groups
         *
         * Function updates the section group data. We can use this function
         * for adding new group to the specific section or remove from the section.
         *
         * @author PH
         * @return void
         */
        updateSectionGroups(state, data) {
          if (data.section) {
            const sectionIndex = this.getters.getSectionIndex(data.section);
            if (-1 !== sectionIndex && state.sections[sectionIndex].hasOwnProperty('groups')) {
              if ('' === data.group) {
                // Add new group.
                if (data.values.id) {
                  state.sections[sectionIndex]['groups'].push({
                    id: data.values.id,
                    title: data.values.title
                  });
                }
              } else if (data.values && data.group) {
                const groupIndex = state.sections[sectionIndex].groups.findIndex(group => group.id === data.group);

                if (-1 !== groupIndex) {
                  Object.keys(data.values).forEach(key => {
                    if (state.sections[sectionIndex].groups[groupIndex].hasOwnProperty(key)) {
                      state.sections[sectionIndex].groups[groupIndex][key] = data.values[key];
                    }
                  });
                }
              }

              if (data.remove === true && data.group) {
                // Remove Group.
                state.sections[sectionIndex].groups = state.sections[sectionIndex].groups.filter(group => {
                  return group.id !== data.group;
                });
              }
            }
          }
        },

        /**
         * Update: Section Settings
         *
         * Function is used for updating settings in specific section.
         *
         * @author PH
         * @return void
         */
        updateSectionSettings(state, data) {
          if (data.section) {
            const sectionIndex = this.getters.getSectionIndex(data.section);
            if (-1 !== sectionIndex) {
              Object.keys(data.values).forEach(key => {
                if (state.sections[sectionIndex]['settings'].hasOwnProperty(key)) {
                  state.sections[sectionIndex]['settings'][key] = data.values[key];
                }
              });
            }
          }
        },

        /**
         * Change: Section
         *
         * Function analyses section switch request and decide
         * if switching it will be possible. The prevoius sections
         * needs to be valid.
         *
         * @author PH
         */
        changeSection(state, data) {
          if (data.id) {
            let changeSection = false;
            const requestedSectionIndex = this.getters.getSectionIndex(data.id);

            if (-1 !== requestedSectionIndex) {
              if (requestedSectionIndex === 0) {
                changeSection = true;
              } else {
                if (state.sections[requestedSectionIndex - 1].valid !== true) {
                  alert('Previous section is not valid!');
                } else {
                  changeSection = true;
                }
              }

              if (changeSection) {
                state.sections.forEach((section, index) => {
                  if (section.id === data.id) {
                    section.active = true;
                    window.scroll({
                      top: 230,
                      behavior: 'smooth'
                    });
                  } else {
                    section.active = false;
                  }
                });
              }
            }
          }
        },

        /**
         * Handle the validation response data. This mutation
         * is fired if the field receive the validation response.
         *
         * Exceptions:
         * 1) Pony: Fill league name based on League ID
         * 2) Little League: Fill league name based on League ID
         *
         * @author PH
         * @return void
         */
        handleValidationResponse(state, data) {
          if (has(data, 'validationResponse')) {
            switch (data.validationResponse.type) {
              case 'pony-csv':
                this.commit('updateInput', {
                  id: 'company[company][league-name]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.leagueName,
                    valid: true
                  }
                });
                break;

              case 'littleleague-api':
                this.commit('updateInput', {
                  id: 'company[company][little-league-name]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.leagueName,
                    valid: true
                  }
                });
                break;

              case 'littlebird-database':
                this.commit('updateInput', {
                  id: 'company[company][phone]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.data.phone,
                    valid: true
                  }
                });
                this.commit('updateInput', {
                  id: 'company[company][street-address]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.data.address,
                    valid: true
                  }
                });
                this.commit('updateInput', {
                  id: 'company[company][city]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.data.city,
                    valid: true
                  }
                });
                this.commit('updateInput', {
                  id: 'company[company][zip-code]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.data.zip,
                    valid: true
                  }
                });
                this.commit('updateInput', {
                  id: 'company[company][state]',
                  section: 'company',
                  values: {
                    value: data.validationResponse.data.state,
                    valid: true
                  }
                });
                break;

              case 'mcneil-api':
                for (const [key, value] of Object.entries(data.validationResponse.data)) {
                  if (value !== '') {
                    const fields = this.getters.getFieldsBy('api_response_key', key, 'company');

                    fields.forEach(field => {
                      if (field.visible) {
                        this.commit('updateInput', {
                          id: field.id,
                          section: 'company',
                          values: {
                            value: value,
                            valid: true
                          }
                        });
                      }
                    });
                  }
                }
                break;
            }
          }
        }
      }
    });
  }
}
